/**
 * GiornoChiusoBlocco.jsx
 * Il Diario di Bordo - SaaS
 * 
 * Componenti per gestire giorni chiusi in NewEntryPage:
 * - GiornoChiusoBlocco: blocco completo compilazione
 * - GiornoChiusuraParzialeWarning: avviso chiusura parziale
 * - AperturaStroardinariaInfo: info apertura straordinaria
 * - ModalConfermaForzatura: modal per coordinatore per forzare compilazione
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  AlertTriangle, 
  Calendar, 
  Lock, 
  Unlock,
  X,
  ChevronRight,
  Info,
  Star
} from 'lucide-react'

/**
 * Blocco completo per giorno chiuso
 * Mostra messaggio e impedisce compilazione (tranne coordinatore con forzatura)
 */
export function GiornoChiusoBlocco({ 
  statoGiorno, 
  isCoordinatore = false,
  onForzaCompilazione
}) {
  const [showModal, setShowModal] = useState(false)
  
  if (!statoGiorno || statoGiorno.aperto) return null
  
  // Determina stile basato su tipologia
  const getStyle = () => {
    switch (statoGiorno.tipologia) {
      case 'festivita':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-300 dark:border-red-700',
          icon: 'text-red-500',
          title: 'text-red-800 dark:text-red-200',
          text: 'text-red-600 dark:text-red-300'
        }
      case 'santo_patrono':
        return {
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          border: 'border-purple-300 dark:border-purple-700',
          icon: 'text-purple-500',
          title: 'text-purple-800 dark:text-purple-200',
          text: 'text-purple-600 dark:text-purple-300'
        }
      case 'chiusura_straordinaria':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-300 dark:border-orange-700',
          icon: 'text-orange-500',
          title: 'text-orange-800 dark:text-orange-200',
          text: 'text-orange-600 dark:text-orange-300'
        }
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800',
          border: 'border-gray-300 dark:border-gray-600',
          icon: 'text-gray-500',
          title: 'text-gray-800 dark:text-gray-200',
          text: 'text-gray-600 dark:text-gray-400'
        }
    }
  }
  
  const style = getStyle()
  
  return (
    <>
      <div className={`rounded-xl border-2 ${style.border} ${style.bg} p-6 text-center`}>
        <div className="flex justify-center mb-4">
          <div className={`p-4 rounded-full ${style.bg}`}>
            <span className="text-4xl">{statoGiorno.emoji || '🚫'}</span>
          </div>
        </div>
        
        <h2 className={`text-xl font-bold ${style.title} mb-2`}>
          Centro Chiuso
        </h2>
        
        <p className={`${style.text} mb-4`}>
          {statoGiorno.motivo}
        </p>
        
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${style.bg} border ${style.border}`}>
          <Lock className={`h-4 w-4 ${style.icon}`} />
          <span className={`text-sm font-medium ${style.text}`}>
            Compilazione non disponibile
          </span>
        </div>
        
        {/* Bottone per coordinatore */}
        {isCoordinatore && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 mb-3">
              Come coordinatore puoi forzare la compilazione
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Unlock className="h-4 w-4" />
              Forza Compilazione
            </button>
          </div>
        )}
        
        {/* Link impostazioni */}
        <div className="mt-4">
          <Link
            to="/settings?tab=calendario"
            className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
          >
            Gestisci calendario chiusure
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      
      {/* Modal conferma forzatura */}
      {showModal && (
        <ModalConfermaForzatura
          statoGiorno={statoGiorno}
          onClose={() => setShowModal(false)}
          onConfirm={() => {
            setShowModal(false)
            onForzaCompilazione?.()
          }}
        />
      )}
    </>
  )
}

/**
 * Warning per chiusura parziale (mattina/pomeriggio)
 */
export function GiornoChiusuraParzialeWarning({ statoGiorno }) {
  if (!statoGiorno?.parziale) return null
  
  const isMattina = statoGiorno.parziale === 'mattina'
  
  return (
    <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 p-4 mb-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-medium text-amber-800 dark:text-amber-200">
            Chiusura Parziale
          </h4>
          <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
            {isMattina 
              ? '🌅 Il centro è chiuso solo la mattina. Puoi compilare per il pomeriggio.'
              : '🌆 Il centro è chiuso solo il pomeriggio. Puoi compilare per la mattina.'
            }
          </p>
          <p className="text-xs text-amber-500 mt-2">
            Motivo: {statoGiorno.motivo}
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Info per apertura straordinaria
 */
export function AperturaStroardinariaInfo({ statoGiorno }) {
  if (statoGiorno?.tipologia !== 'apertura_straordinaria') return null
  
  return (
    <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 p-4 mb-4">
      <div className="flex items-start gap-3">
        <Star className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-medium text-green-800 dark:text-green-200">
            Apertura Straordinaria
          </h4>
          <p className="text-sm text-green-600 dark:text-green-300 mt-1">
            ⭐ {statoGiorno.motivo || 'Il centro è aperto in via straordinaria'}
          </p>
          {(statoGiorno.orarioApertura || statoGiorno.orarioChiusura) && (
            <p className="text-xs text-green-500 mt-2">
              Orario: {statoGiorno.orarioApertura} - {statoGiorno.orarioChiusura}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Modal conferma forzatura compilazione
 */
export function ModalConfermaForzatura({ statoGiorno, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Conferma Forzatura
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg mb-4">
            <span className="text-2xl">{statoGiorno?.emoji || '🚫'}</span>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {statoGiorno?.motivo}
              </p>
              <p className="text-xs text-gray-500">
                Tipologia: {statoGiorno?.tipologia?.replace('_', ' ')}
              </p>
            </div>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Sei sicuro di voler compilare una registrazione in un giorno di chiusura?
          </p>
          
          <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
            ⚠️ Questa azione verrà registrata nel log delle attività.
          </p>
        </div>
        
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="btn-secondary">
            Annulla
          </button>
          <button onClick={onConfirm} className="btn-primary bg-orange-600 hover:bg-orange-700">
            <Unlock className="h-4 w-4 mr-2" />
            Forza Compilazione
          </button>
        </div>
      </div>
    </div>
  )
}

export default GiornoChiusoBlocco
